// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { until } from 'lit/directives/until.js';
import { isEmpty, sprintf } from '@automator/utilities';
import apiFetch from '@wordpress/api-fetch';

// Add the nonce as middleware. This automatically adds the `X-WP-Nonce` header to requests.
apiFetch.use( apiFetch.createNonceMiddleware( UncannyAutomatorBackend.rest.nonce ) );

// Import style for web component
import styles from './web-component-style.scss';

// Get translatable strings
const strings = UncannyAutomatorBackend.components.userCard.i18n;

// eslint-disable-next-line require-jsdoc
@customElement( 'uo-user-card' )
export class AutomatorUserCard extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: Number, attribute: 'user-id' } ) accessor userID;

	// eslint-disable-next-line require-jsdoc
	render() {
		// Check if the required ID exists
		if ( isEmpty( this.userID ) ) {
			return html`${ strings.idRequired }`;
		}

		return html`${ until(
			apiFetch( {
				url: `${ UncannyAutomatorBackend.rest.base }automator/v1/user/${ this.userID }`
			} )
				.then( ( response ) => {
					return this._templateUser( {
						avatar: response.data.avatar,
						name: response.data.display_name,
						email: response.data.email
					} );
				} ).catch( ( error ) => {
					// Check if the user doesn't exist
					if ( error.data.error_code === 404 ) {
						return this._templateUserNotFound;
					} else {
						return html`${ strings.unknownError }`;
					}
				} ),
			this._templateLoading
		) }`;
	}

	/**
	 * Returns the template that displays the user data
	 *
	 * @param {Object} data
	 * @param {String} data.avatar The URL of the user avatar
	 * @param {String} data.name The name of the user
	 * @param {String} data.email The email of the user
	 * @return {TemplateResult} The template with the user data
	 */
	_templateUser( { avatar, name, email } ) {
		return html`
			<div class="user">
				<img class="user__avatar" src="${ avatar }">
				<div class="user__name">${ name }</div>
				<div class="user__email">${ email }</div>
			</div>
		`;
	}

	/**
	 * Returns the template displayed while we're making the call to retrieve the user data
	 *
	 * @return {TemplateResult} The loading placeholder
	 */
	get _templateLoading() {
		return html`
			<div class="user user--placeholder">
				<uo-placeholder class="user__avatar-placeholder" width="32px" height="32px"></uo-placeholder>
				<div class="user__name-placeholder">
					<uo-placeholder width="50px"></uo-placeholder> <uo-placeholder width="70px"></uo-placeholder>
				</div>
				<uo-placeholder class="user__email-placeholder"></uo-placeholder>
			</div>
		`;
	}

	/**
	 * Returns the template displayed while the user data doesn't exist
	 *
	 * @return {TemplateResult} The template with the user not found
	 */
	get _templateUserNotFound() {
		return html`
			<div class="user">
				<div class="user__icon">
					<uo-icon id="user-xmark" size="16"></uo-icon>
				</div>
				<div class="user__name">${ strings.userNotFound }</div>
				<div class="user__email">${ sprintf( strings.userID, this.userID ) }</div>
			</div>
		`;
	}
}
